/**
 * 国家信息相关的 store
 */
import { create } from 'zustand';
import axios from 'axios';
import { CountryItem, normalizeCountry, CountryRaw } from '@/utils/country';

interface CountryInfoState {
  countryList: CountryItem[];
  loading: boolean;
  fetchCountryList: () => void;
  reloadCountryList: () => void;
  getCountryListWithCache: () => Promise<CountryItem[]>;
  /** 根据国家代码、名称或别名查找国家，支持 "CN"、"China"、"United States" 等 */
  getCountryByCodeOrName: (value: string) => CountryItem | undefined;
}

let countryFetchPromiseCache: Promise<CountryItem[]> | null = null;
const useCountryInfoStore = create<CountryInfoState>((set, get) => ({
  countryList: [],
  loading: false,
  fetchCountryList: async () => {
    if (!countryFetchPromiseCache) {
      set({ loading: true });
      countryFetchPromiseCache = (async () => {
        try {
          const response = await axios.request({
            method: 'GET',
            url: '/wk/api/country/list',
          });
          const data = response?.data as Record<string, unknown> | undefined;
          const payload = (data?.data as unknown) || (data?.result as unknown) || (data?.list as unknown) || data;
          const list = Array.isArray(payload)
            ? payload
            : Array.isArray((payload as Record<string, unknown>)?.list)
              ? ((payload as Record<string, unknown>).list as unknown[])
              : [];
          const normalizedList = list.map(item => normalizeCountry(item as CountryRaw));
          set({ countryList: normalizedList });
          return normalizedList;
        } catch (error) {
          console.error('Failed to fetch country list:', error);
          countryFetchPromiseCache = null;
          return [];
        } finally {
          set({ loading: false });
        }
      })();
    }
    return countryFetchPromiseCache;
  },
  reloadCountryList: () => {
    countryFetchPromiseCache = null;
    set({ countryList: [] });
    const { fetchCountryList } = get();
    return fetchCountryList();
  },
  async getCountryListWithCache() {
    const { fetchCountryList } = get();
    await fetchCountryList();
    return countryFetchPromiseCache || Promise.resolve([]);
  },
  getCountryByCodeOrName(value: string) {
    const { countryList } = get();
    if (!value?.trim()) return undefined;
    const normalized = value.trim();
    const lower = normalized.toLowerCase();
    return countryList.find(item => {
      if (item.code?.toUpperCase() === normalized.toUpperCase()) return true;
      if (item.name === normalized || item.enName === normalized || item.wmcode === normalized) return true;
      if (item.aliases?.some(a => a?.toLowerCase() === lower)) return true;
      return false;
    });
  },
}));

export default useCountryInfoStore;
